"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState, } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";
const STEPS = ["new", "code", "done"];
const CODE_LENGTH = 6;
const RESEND_SECONDS = 45;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
async function readError(res) {
    return (await res.json().catch(() => ({})));
}
function fmtWait(seconds) {
    const s = Math.max(0, seconds);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
/**
 * Bloom 2.0 «Change email» (Edit profile → C4–C6): new address → 6-digit code
 * sent to it → done. Opens on top of Edit profile.
 */
export default function ChangeEmailDialog({ open, onOpenChange, currentEmail, onChanged, }) {
    const t = useTranslations("settings.changeEmail");
    const { setToken, refetchUser } = useAuth();
    const queryClient = useQueryClient();
    const [step, setStep] = useState("new");
    const [email, setEmail] = useState("");
    const [touched, setTouched] = useState(false);
    const [sendErr, setSendErr] = useState("");
    const [sending, setSending] = useState(false);
    const [code, setCode] = useState("");
    const [codeFocus, setCodeFocus] = useState(false);
    const [busy, setBusy] = useState(false);
    const [codeErr, setCodeErr] = useState("");
    const [resendAt, setResendAt] = useState(0);
    const [now, setNow] = useState(() => Date.now());
    const [oldEmail, setOldEmail] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const newRef = useRef(null);
    const codeRef = useRef(null);
    // Fresh start every time the window opens
    useEffect(() => {
        if (!open)
            return;
        setStep("new");
        setEmail("");
        setTouched(false);
        setSendErr("");
        setCode("");
        setCodeErr("");
        setBusy(false);
        setSending(false);
    }, [open]);
    // Resend countdown
    useEffect(() => {
        if (!open || step !== "code")
            return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [open, step]);
    useEffect(() => {
        if (!open)
            return;
        const el = step === "new" ? newRef.current : codeRef.current;
        const id = setTimeout(() => el === null || el === void 0 ? void 0 : el.focus(), 60);
        return () => clearTimeout(id);
    }, [open, step]);
    const value = email.trim();
    const valid = EMAIL_PATTERN.test(value);
    const same = value.toLowerCase() === currentEmail.trim().toLowerCase();
    const newOk = valid && !same;
    const localErr = touched && value ? (!valid ? t("invalid") : same ? t("same") : "") : "";
    const newErr = localErr || sendErr;
    const left = Math.max(0, Math.ceil((resendAt - now) / 1000));
    const idx = STEPS.indexOf(step);
    const canVerify = code.length === CODE_LENGTH && !busy;
    const send = useCallback(async () => {
        var _a;
        if (sending)
            return;
        setSending(true);
        setSendErr("");
        try {
            const res = await apiFetch("/v1/me/email/change/send-code", {
                method: "POST",
                body: JSON.stringify({ email: value }),
            });
            if (res.ok) {
                setStep("code");
                setCode("");
                setCodeErr("");
                setBusy(false);
                setResendAt(Date.now() + RESEND_SECONDS * 1000);
                setNow(Date.now());
                return true;
            }
            const err = await readError(res);
            if (res.status === 429) {
                const wait = (_a = err.retry_after_seconds) !== null && _a !== void 0 ? _a : RESEND_SECONDS;
                if (step === "code") {
                    setResendAt(Date.now() + wait * 1000);
                    setNow(Date.now());
                }
                else {
                    setSendErr(t("wait", { time: fmtWait(wait) }));
                }
                return false;
            }
            const byCode = {
                invalid_email: t("invalid"),
                same_email: t("same"),
                email_taken: t("taken"),
            };
            const msg = (err.code && byCode[err.code]) || t("failed");
            if (step === "code")
                setCodeErr(msg);
            else
                setSendErr(msg);
            return false;
        }
        catch (_b) {
            if (step === "code")
                setCodeErr(t("failed"));
            else
                setSendErr(t("failed"));
            return false;
        }
        finally {
            setSending(false);
        }
    }, [sending, step, t, value]);
    const submitNew = () => {
        if (newOk)
            void send();
        else
            setTouched(true);
    };
    const verify = async (c) => {
        var _a, _b, _c;
        if (c.length < CODE_LENGTH || busy)
            return;
        setBusy(true);
        setCodeErr("");
        try {
            const res = await apiFetch("/v1/me/email/change/confirm", {
                method: "POST",
                body: JSON.stringify({ email: value, code: c }),
            });
            if (!res.ok) {
                const err = await readError(res);
                if (res.status === 429) {
                    setCodeErr(t("tooMany", { time: fmtWait((_a = err.retry_after_seconds) !== null && _a !== void 0 ? _a : 60) }));
                }
                else if (err.code === "invalid_code") {
                    setCodeErr(t("wrong"));
                }
                else if (err.code === "email_taken") {
                    setCodeErr(t("taken"));
                }
                else {
                    setCodeErr(t("failed"));
                }
                return;
            }
            const data = (await res.json());
            // The old access token still carries the old address: switch to the new pair
            if ((_b = data.tokens) === null || _b === void 0 ? void 0 : _b.refresh_token) {
                try {
                    localStorage.setItem("refresh_token", data.tokens.refresh_token);
                }
                catch (_d) {
                    // ignore storage errors: the next refresh signs the doctor out
                }
            }
            if ((_c = data.tokens) === null || _c === void 0 ? void 0 : _c.access_token) {
                setToken(data.tokens.access_token);
                void refetchUser();
            }
            setOldEmail(data.old_email || currentEmail);
            setNewEmail(data.new_email || value);
            setStep("done");
            onChanged(data.new_email || value);
            void queryClient.invalidateQueries({ queryKey: ["doctor", "me"] });
        }
        catch (_e) {
            setCodeErr(t("failed"));
        }
        finally {
            setBusy(false);
        }
    };
    const onCodeChange = (raw) => {
        const v = raw.replace(/\D/g, "").slice(0, CODE_LENGTH);
        setCode(v);
        setCodeErr("");
        if (v.length === CODE_LENGTH)
            void verify(v);
    };
    const back = () => {
        setStep("new");
        setCode("");
        setCodeErr("");
        setSendErr("");
    };
    const active = Math.min(code.length, CODE_LENGTH - 1);
    const strong = (chunks) => (_jsx("span", { className: "font-semibold text-ink", children: chunks }));
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogContent, { hideClose: true, 
            // Lighter scrim: it sits on top of Edit profile (handoff 0.32)
            overlayClassName: "bg-[rgba(16,24,32,0.32)]", className: "flex w-[440px] max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden rounded-card p-0 outline-none sm:max-w-[440px] sm:p-0", 
            // Start in the address field, not on the close button (Enter would close the window)
            onOpenAutoFocus: (e) => {
                var _a;
                e.preventDefault();
                (_a = newRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, children: [_jsxs("div", { className: "flex flex-col gap-3 pl-6 pr-4 pt-4", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "min-w-0 flex-1 text-overline font-semibold uppercase tracking-[0.05em] text-ink-muted", children: step === "done"
                                        ? t("completed")
                                        : t("stepLabel", { n: idx + 1, total: STEPS.length }) }), _jsxs(DialogClose, { className: "inline-flex h-control-sm w-control-sm shrink-0 items-center justify-center rounded-nav text-ink transition-colors duration-hover hover:bg-ghost-hover active:bg-ghost-active focus:outline-none focus-visible:bg-ghost-hover [&>svg]:transition-transform [&>svg]:duration-hover hover:[&>svg]:rotate-90", children: [_jsx("svg", { viewBox: "-8 -8 32 32", className: "h-8 w-8", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M4 4l8 8M12 4l-8 8", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round" }) }), _jsx("span", { className: "sr-only", children: t("close") })] })] }), _jsx("div", { className: "mr-2 grid grid-cols-3 gap-1", children: ["segNew", "segVerify", "segDone"].map((key, i) => (_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx("div", { className: "h-1 overflow-hidden rounded-pill bg-btn-secondary", children: _jsx("div", { className: "h-full rounded-pill bg-brand-data transition-[width] duration-[320ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]", style: { width: i <= idx ? "100%" : "0%" } }) }), _jsx("div", { className: cn("text-caption transition-colors duration-hover", i <= idx ? "text-ink" : "text-ink-muted", i === idx ? "font-semibold" : "font-medium"), children: t(key) })] }, key))) })] }), step === "new" ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex animate-bloom-fade flex-col gap-4 p-6", children: [_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(DialogTitle, { className: "t-h2 text-ink", children: t("title") }), _jsx(DialogDescription, { className: "text-body leading-[1.5] text-ink-2 [text-wrap:pretty]", children: t.rich("hint", { email: currentEmail, b: strong }) })] }), _jsxs("div", { className: "min-w-0", children: [_jsx("label", { htmlFor: "change-email-new", className: "mb-2 block text-caption font-semibold text-ink-2", children: t("newEmail") }), _jsx("input", { id: "change-email-new", ref: newRef, type: "email", autoComplete: "email", placeholder: "name@clinic.com", value: email, onChange: (e) => {
                                                setEmail(e.target.value);
                                                setSendErr("");
                                            }, onBlur: () => setTouched(true), onKeyDown: (e) => {
                                                if (e.key === "Enter")
                                                    submitNew();
                                            }, "aria-invalid": !!newErr, className: cn("h-control w-full rounded-control border bg-surface px-[14px] text-body text-ink outline-none transition-colors duration-hover placeholder:text-ink-muted", newErr
                                                ? "border-danger"
                                                : "border-control hover:border-control-hover focus:border-control-focus") }), newErr ? (_jsx("div", { className: "mt-2 text-caption text-danger", children: newErr })) : null] })] }), _jsxs("div", { className: "flex justify-end gap-2 border-t border-line px-6 py-4", children: [_jsx(Button, { className: "px-[18px]", onClick: () => onOpenChange(false), children: t("cancel") }), _jsx(Button, { variant: "accent", "aria-disabled": !newOk || sending, onClick: submitNew, className: cn((!newOk || sending) &&
                                        "cursor-default bg-btn-secondary text-ink-muted hover:bg-btn-secondary active:bg-btn-secondary"), children: t("send") })] })] })) : null, step === "code" ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex animate-bloom-fade flex-col gap-4 p-6", children: [_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(DialogTitle, { className: "t-h2 text-ink", children: t("codeTitle") }), _jsx(DialogDescription, { className: "text-body leading-[1.5] text-ink-2 [text-wrap:pretty]", children: t.rich("codeHint", { email: value, b: strong }) })] }), _jsxs("div", { className: "relative", children: [_jsx("div", { className: "grid grid-cols-6 gap-2", children: Array.from({ length: CODE_LENGTH }, (_, i) => {
                                                var _a;
                                                return (_jsx("div", { className: cn("flex h-14 items-center justify-center rounded-control border font-display text-heading font-bold transition-colors duration-hover", codeErr
                                                        ? "border-danger bg-danger-bg text-danger"
                                                        : cn("text-ink", codeFocus && i === active && !busy
                                                            ? "border-control-focus"
                                                            : code[i]
                                                                ? "border-control-hover"
                                                                : "border-control", code[i] ? "bg-well" : "bg-transparent")), children: (_a = code[i]) !== null && _a !== void 0 ? _a : "" }, i));
                                            }) }), _jsx("input", { ref: codeRef, value: code, onChange: (e) => onCodeChange(e.target.value), onFocus: () => setCodeFocus(true), onBlur: () => setCodeFocus(false), inputMode: "numeric", autoComplete: "one-time-code", maxLength: CODE_LENGTH, "aria-label": t("codeAria"), className: "absolute inset-0 h-full w-full cursor-text border-0 p-0 text-[16px] opacity-0" })] }), _jsx("div", { className: "flex min-h-5 items-center gap-2 text-small", "aria-live": "polite", children: busy ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "h-3.5 w-3.5 animate-spin rounded-pill border-2 border-btn-secondary border-t-ink" }), _jsx("span", { className: "text-ink-2", children: t("checking") })] })) : codeErr ? (_jsx("span", { className: "text-danger", children: codeErr })) : left > 0 ? (_jsx("span", { className: "text-ink-2", children: t("resendIn", { time: fmtWait(left) }) })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: "text-ink-2", children: t("didntGet") }), _jsx("button", { type: "button", disabled: sending, onClick: () => void send(), className: "font-semibold text-ink underline underline-offset-[3px]", children: t("resend") })] })) })] }), _jsxs("div", { className: "flex items-center gap-2 border-t border-line px-6 py-4", children: [_jsx(Button, { className: "px-[18px]", onClick: back, children: t("changeAddress") }), _jsx("div", { className: "flex-1" }), _jsx(Button, { variant: "accent", "aria-disabled": !canVerify, onClick: () => void verify(code), className: cn(!canVerify &&
                                        "cursor-default bg-btn-secondary text-ink-muted hover:bg-btn-secondary active:bg-btn-secondary"), children: t("verify") })] })] })) : null, step === "done" ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex animate-bloom-fade flex-col gap-4 p-6", children: [_jsx("span", { className: "inline-flex h-12 w-12 animate-bloom-rise items-center justify-center rounded-pill bg-brand", children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", "aria-hidden": true, className: "stroke-brand-ink", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M4.5 10.5l3.5 3.5 7.5-8" }) }) }), _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(DialogTitle, { className: "t-h2 text-ink", children: t("doneTitle") }), _jsx(DialogDescription, { className: "text-body leading-[1.5] text-ink-2 [text-wrap:pretty]", children: t("doneHint") })] }), _jsxs("div", { className: "flex flex-col gap-2 rounded-control bg-well px-4 py-3", children: [_jsxs("div", { className: "grid grid-cols-[48px_minmax(0,1fr)] items-baseline gap-3", children: [_jsx("span", { className: "text-overline font-semibold uppercase tracking-[0.05em] text-ink-muted", children: t("was") }), _jsx("span", { className: "truncate text-body text-ink-2 line-through", children: oldEmail })] }), _jsxs("div", { className: "grid grid-cols-[48px_minmax(0,1fr)] items-baseline gap-3", children: [_jsx("span", { className: "text-overline font-semibold uppercase tracking-[0.05em] text-ink-muted", children: t("now") }), _jsx("span", { className: "truncate text-body font-semibold text-ink", children: newEmail })] })] })] }), _jsx("div", { className: "flex justify-end gap-2 border-t border-line px-6 py-4", children: _jsx(Button, { variant: "accent", onClick: () => onOpenChange(false), children: t("done") }) })] })) : null] }) }));
}
